import type { IModal, IModalConfig } from "../types"
import { notNullish } from "@vueuse/core";
import { v4 } from 'uuid';
import { useAnimationStep } from "@/utils/animation/useAnimationStep";
import { useKeyboard } from "@/utils/hooks/useKeyboard";
import { EventProducer } from "@/utils/event";
import { MODAL_ANIMATION_DURATION } from "../constants";
import type { UnwrapNestedRefs } from "vue";
import { unwrapNestedRefs } from "@/utils/object";

export type ModalPatchFun = (old: UnwrapNestedRefs<IModal>) => UnwrapNestedRefs<IModal>; 

export type UseModals = {
  modals: Ref<Map<string, MaybeRef<IModal>>>;
  ids: Ref<Set<string>>;
  items: ComputedRef<MaybeRef<IModal>[]>;
  count: Ref<number>;
  push: (cfg: IModalConfig) => Promise<void>;
  pop: (id?: string) => Promise<void>;
  patch: (fn: ModalPatchFun, id?: string) => void;
  clear: () => Promise<void>;
  events: EventProducer<ModalEventMap>;
}

export enum EModalEvent {
  CLOSE = 'CLOSE',
  OPEN = 'OPEN'
}

export type ModalEventMap = {
  [EModalEvent.CLOSE]: { id: string; };
  [EModalEvent.OPEN]: { id: string; };
}

export const useModals = ():UseModals => {
  const events = new EventProducer<ModalEventMap>();
  const modals = ref<Map<string, Ref<IModal>>>(new Map());
  const ids = ref<Set<string>>(new Set());
  const trash = new Map<string, boolean>();
  const count = ref<number>(0);

  const items = computed(() => Array.from(ids.value).map((id) => modals.value.get(id)).filter(notNullish));

  const push = (cfg: IModalConfig) => {
    const id = v4();
    const modal: IModal = {
      ...cfg,
      id,
      animation: useAnimationStep({
        initial: 0,
        duration: MODAL_ANIMATION_DURATION
      })
    };
    events.emit(EModalEvent.OPEN, { id: id });
    const modalRef = shallowRef<IModal>(modal);
    modals.value.set(id, modalRef);
    ids.value.add(id);
    count.value = count.value + 1;

    return new Promise<void>((resolve) => {
      modalRef.value.animation.to(1.0).then(() => {
        resolve();
      });
    })
  }

  const pop = (id?: string): Promise<void> => {
    const idz = Array.from(ids.value);
    const modalId = id || idz[idz.length-1];
    if (!modalId || trash.has(modalId)) return Promise.resolve();
    const modal =  unref(modals.value.get(modalId));
    if (!modal) return Promise.resolve();
    events.emit(EModalEvent.CLOSE, { id: modalId });
    count.value = Math.max(0, count.value-1);
    trash.set(modalId, true);

    return new Promise<void>((resolve) => {
      modal.animation.to(0.0).then(() => {
        if (modal.onClose) {
          modal.onClose();
        }
        ids.value.delete(modalId);
        modals.value.delete(modalId);
        trash.delete(modalId);
        resolve();
      });
    })
  }

  const clear = async () => {
    const idz = Array.from(ids.value);
    await Promise.all(idz.map(async (id) => await pop(id)));
  }

  const patch =  (fn: ModalPatchFun, id?: string) => {
    const idz = Array.from(ids.value);
    const modalId = id || idz[idz.length-1];
    if (!modalId) return;
    const modal = modals.value.get(modalId);
    if (!modal) return;
    if (!isRef(modal)) {
      console.error(`Expected modal to be ref`);
      return;
    }

    const unwrapped = unwrapNestedRefs(modal.value);
    const next = fn(unwrapped);

    const m = modal.value as unknown as any;
    for (const [key, value] of Object.entries(next)) {
      const old = m[key];
      if (isRef(old)) {
        old.value = unref(value);
        continue;
      }
      m[key] = unref(value);
    }
   // modal.value = fn(modal.value);
  }


  const kb = useKeyboard();
  kb.addEventListener('keydown', (btn) => {
    if (btn.key === 'Escape') {
      pop();
    }
  })
  
  return { modals, ids, push, pop, items, events, count, clear, patch };
}


export const globalModals = useModals();
